import React from "react";
import { ArrowRight, Menu, X, User, Mail, MapPin } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { SOCIAL_ICONS } from "@/components/site/socialIcons";
import { LangSwitch } from "../shared/TemplateShell";
import { NavMore, useInlineNavSplit } from "../shared/InlineNav";
import { useSiteContent } from "../shared/useSiteData";

/**
 * Clinical / Medical — the theme's CHROME (header + footer), in one place.
 *
 * Extracted from ClinicalTemplate so the homepage and the interior pages
 * (templates/ThemedPageLayout.jsx) render the SAME two components (BUG-006 / BUG-032).
 *
 * Idiom: the DOUBLE header — a dark utility strip (contact details + language) above a white
 * sticky nav with a rounded accent-blue CTA. The mobile panel drops below the nav as a white
 * sheet with slate dividers, the same vocabulary as the theme's cards.
 */

export const CLINICAL_MAIN_CLASS = "min-h-screen bg-white text-[var(--tpl-text)]";

/**
 * The FR/EN/ES switch: the selected language is a FILLED chip, never a colour change alone.
 *
 * It lives on the dark utility strip, so `bg-white/20` was legible — but barely, and it left the
 * active language reading as white-on-slate like its two neighbours. The solid white chip with
 * primary-blue type is the theme's white-card-on-blue vocabulary, and it is unambiguous.
 */
const LANG_ACTIVE = "bg-white text-[var(--tpl-primary)]";
const LANG_IDLE = "opacity-60 hover:opacity-100";

/**
 * How many of the ten nav links this theme can draw inline (BUG-045 — see
 * shared/InlineNav.jsx for why a fixed count and not a breakpoint).
 *
 * The budget: 14px semibold labels with `gap-6` (24px) cost ~100px each, beside the bold
 * serif wordmark and the accent CTA (the account link and the language chips live on the
 * utility strip above, so they cost this row nothing). Measured with all ten links, in
 * French, on a 24-character practice name: the bar overflowed by 434px at 1024px and by
 * 163px at 1536px. Removing k links frees ~100k against a "More" pill costing ~70, so
 * 1024px needs k>=6 and 1536px needs k>=3 — 3 and 6 leave a link's worth of margin.
 */
const INLINE_CAPACITY = { lg: 3, xl: 6 };

export const ClinicalHeader = ({ toggles = {} }) => {
  const c = useSiteContent(toggles);
  const tt = useT("templates");
  const nav = c.navModel;
  const links = [...nav.sections, ...nav.pages];
  const { inline, overflow } = useInlineNavSplit(nav, INLINE_CAPACITY);
  const [open, setOpen] = React.useState(false);

  return (
    <>
      {/* UTILITY STRIP */}
      <div className="text-white text-xs" style={{ background: "var(--tpl-primary)" }} data-testid="site-utility-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <span className="inline-flex items-center gap-1.5"><Mail size={12} /> {c.contact.info.email}</span>
            <span className="hidden sm:inline-flex items-center gap-1.5"><MapPin size={12} /> {c.contact.info.place}</span>
          </div>
          <div className="flex items-center gap-4">
            {nav.account && (
              <a href={nav.account.href} data-testid="nav-client-portal" className="inline-flex items-center gap-1.5 hover:underline whitespace-nowrap">
                <User size={12} /> {nav.account.label}
              </a>
            )}
            <LangSwitch lang={c.lang} setLang={c.setLang} activeClass={LANG_ACTIVE} idleClass={LANG_IDLE} />
          </div>
        </div>
      </div>

      {/* NAV */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200" data-testid="site-navbar">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
          <a href={nav.home} className="tpl-head text-2xl font-bold shrink-0" style={{ color: "var(--tpl-primary)" }} data-testid="logo-home">{nav.brand}</a>
          <nav className="hidden lg:flex items-center gap-6 text-sm font-semibold flex-1 justify-center">
            {inline.map((l) => (
              <a key={l.key} href={l.href} data-testid={`nav-link-${l.key}`} className="hover:text-[var(--tpl-accent)] transition-colors whitespace-nowrap">{l.label}</a>
            ))}
            <NavMore
              items={overflow}
              className="text-sm font-semibold hover:text-[var(--tpl-accent)] transition-colors"
              menuClassName="bg-white rounded-md border border-slate-200 shadow-lg text-sm font-semibold"
              itemClassName="hover:text-[var(--tpl-accent)] transition-colors"
            />
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            {nav.book && (
              <a href={nav.book.href} data-testid="nav-book-cta" className="hidden sm:inline-flex items-center gap-1.5 rounded-md px-5 py-2.5 text-sm font-semibold text-white hover:brightness-110 transition whitespace-nowrap" style={{ background: "var(--tpl-accent)" }}>{nav.book.label}</a>
            )}
            <button
              className="lg:hidden"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label={open ? tt("shared.nav.closeMenu") : tt("shared.nav.openMenu")}
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-200 px-6 py-4" data-testid="mobile-menu">
            <div className="flex flex-col divide-y divide-slate-100 text-sm font-semibold">
              {links.map((l) => (
                <a key={l.key} href={l.href} onClick={() => setOpen(false)} className="py-3">{l.label}</a>
              ))}
              {nav.account && (
                <a href={nav.account.href} onClick={() => setOpen(false)} className="py-3 inline-flex items-center gap-2">
                  <User size={14} /> {nav.account.label}
                </a>
              )}
            </div>
            {nav.book && (
              <a href={nav.book.href} onClick={() => setOpen(false)} className="mt-4 inline-flex items-center gap-1.5 rounded-md px-5 py-3 text-sm font-semibold text-white" style={{ background: "var(--tpl-accent)" }}>{nav.book.label} <ArrowRight size={14} /></a>
            )}
          </div>
        )}
      </header>
    </>
  );
};

/**
 * The 4-column layout was already right; what it listed was not. The column headings
 * "Navigation" and "Rendez-vous" were hardcoded (the second in French, on a site that also
 * ships in EN and ES) and the nav column repeated the header's four anchors. Both now come
 * from the shared model.
 */
export const ClinicalFooter = ({ toggles = {} }) => {
  const c = useSiteContent(toggles);
  const f = c.footerModel;
  const book = c.navModel.book;

  return (
    <footer className="bg-white border-t border-slate-200 px-6 md:px-10 py-12" data-testid="site-footer">
      <div className="max-w-7xl mx-auto grid sm:grid-cols-2 md:grid-cols-4 gap-8 text-sm">
        <div>
          <div className="tpl-head text-xl font-bold" style={{ color: "var(--tpl-primary)" }}>{f.brand}</div>
          {/* BUG-210: no dictionary sample any more — an unset tagline is a fact the
              practice hasn't stated, not filler text. */}
          {f.tagline && <p className="mt-3 text-slate-500">{f.tagline}</p>}
          {f.socials.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {f.socials.map((s) => {
                const Icon = SOCIAL_ICONS[s.key];
                return (
                  <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-testid={`footer-social-${s.key}`} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[var(--tpl-accent)] hover:text-[var(--tpl-accent)] transition-colors">
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <div className="font-bold uppercase text-xs tracking-widest text-slate-400">{f.headings.navigate}</div>
          <div className="mt-3 space-y-2">
            {f.sections.map((s) => (
              <a key={s.key} href={s.href} data-testid={`footer-section-${s.key}`} className="block text-slate-600 hover:text-[var(--tpl-accent)]">{s.label}</a>
            ))}
          </div>
        </div>
        <div>
          <div className="font-bold uppercase text-xs tracking-widest text-slate-400">{f.headings.explore}</div>
          <div className="mt-3 space-y-2">
            {f.pages.map((p) => (
              <a key={p.key} href={p.href} data-testid={`footer-page-${p.key}`} className="block text-slate-600 hover:text-[var(--tpl-accent)]">{p.label}</a>
            ))}
          </div>
        </div>
        <div>
          <div className="font-bold uppercase text-xs tracking-widest text-slate-400">{f.headings.contact}</div>
          <div className="mt-3 space-y-2 text-slate-600">
            <a href={`mailto:${f.contact.email}`} className="block break-all hover:text-[var(--tpl-accent)]">{f.contact.email}</a>
            <div>{f.contact.place}</div>
          </div>
          {book && (
            <a href={book.href} className="mt-4 inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-white text-sm font-semibold" style={{ background: "var(--tpl-accent)" }}>{book.label} <ArrowRight size={14} /></a>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-slate-100 text-xs text-slate-400" data-testid="footer-copyright">© {f.year} {f.brand} — {f.rights}</div>
    </footer>
  );
};

/** The chrome contract templates/registry.js registers for t5_clinical. */
export const CLINICAL_CHROME = {
  Header: ClinicalHeader,
  Footer: ClinicalFooter,
  mainClass: CLINICAL_MAIN_CLASS,
};

export default CLINICAL_CHROME;
